import React from 'react';

type Accent = 'cyan' | 'magenta' | 'green' | 'purple' | 'amber';

const accentMap: Record<Accent, { color: string; rgb: string }> = {
  cyan: { color: '#22e6ff', rgb: '34,230,255' },
  magenta: { color: '#ff3df0', rgb: '255,61,240' },
  green: { color: '#2bffb0', rgb: '43,255,176' },
  purple: { color: '#9d5cff', rgb: '157,92,255' },
  amber: { color: '#ffb13d', rgb: '255,177,61' },
};

interface NodeCardProps {
  title?: string;
  subtitle?: string;
  badge?: string;
  icon?: React.ReactNode;
  accent?: Accent;
  ports?: boolean;
  className?: string;
  children?: React.ReactNode;
}

/**
 * NodeCard — a card styled like an n8n workflow node: rounded panel with a
 * neon icon chip, an optional title/subtitle header row, connector ports on
 * the sides, and a neon glow on hover.
 */
const NodeCard: React.FC<NodeCardProps> = ({
  title,
  subtitle,
  badge,
  icon,
  accent = 'cyan',
  ports = false,
  className = '',
  children,
}) => {
  const a = accentMap[accent];

  return (
    <div
      className={`n8n-node p-5 ${className}`}
      style={
        {
          // tint the hover border/glow toward the chosen accent
          ['--accent' as any]: a.color,
        } as React.CSSProperties
      }
    >
      {ports && (
        <>
          <span
            className="node-port in"
            style={{ borderColor: a.color, color: a.color }}
          />
          <span
            className="node-port out"
            style={{ borderColor: a.color, color: a.color }}
          />
        </>
      )}

      {(icon || title || badge) && (
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            {icon && (
              <span
                className="node-chip shrink-0"
                style={{
                  borderColor: `rgba(${a.rgb},0.4)`,
                  boxShadow: `0 0 22px -8px rgba(${a.rgb},0.9)`,
                  background: `linear-gradient(135deg, rgba(${a.rgb},0.22), rgba(157,92,255,0.14))`,
                  color: a.color,
                }}
              >
                {icon}
              </span>
            )}
            {(title || subtitle) && (
              <div>
                {title && (
                  <h3 className="font-display text-lg font-semibold leading-tight text-ink-main">
                    {title}
                  </h3>
                )}
                {subtitle && (
                  <p className="font-mono text-xs text-ink-muted">{subtitle}</p>
                )}
              </div>
            )}
          </div>
          {badge && (
            <span
              className="rounded-md px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider"
              style={{
                color: a.color,
                background: `rgba(${a.rgb},0.10)`,
                border: `1px solid rgba(${a.rgb},0.35)`,
              }}
            >
              {badge}
            </span>
          )}
        </div>
      )}

      {children}
    </div>
  );
};

export default NodeCard;
